import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import SchedulingPage from './SchedulingPage';
import { 
  Package, 
  Shield, 
  Eye, 
  BarChart3, 
  Users, 
  CheckCircle, 
  TrendingUp,
  Clock,
  FileText,
  AlertTriangle,
  ChevronRight,
  Smartphone,
  ArrowRight,
  HardHat,
  Wrench,
  Activity
} from 'lucide-react';

const IndustrialSafety: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<'landing' | 'scheduling'>('landing');

  const handleScheduleDemo = () => {
    setCurrentPage('scheduling');
  };

  const handleBackToLanding = () => {
    setCurrentPage('landing');
  };

  if (currentPage === 'scheduling') {
    return <SchedulingPage onBack={handleBackToLanding} />;
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Industrial Safety Monitoring | EHS Compliance & OSHA Logs"
        description="Work-hours phone monitoring for manufacturing, construction, and field ops. OSHA-ready logs, multi-site visibility, no message access. Built for EHS teams."
        canonical="https://nixspaces.com/industrial-safety-monitoring"
        keywords="industrial safety monitoring, EHS phone monitoring, OSHA compliance, construction safety, field service monitoring"
      />

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center space-x-3">
              <img src="/image.png" alt="Nix Logo" className="h-12 w-auto mt-1" />
              <span className="text-2xl font-bold text-gray-900">Spaces</span>
            </Link>
            <nav className="hidden md:flex space-x-8">
              <a href="#features" className="text-gray-600 hover:text-blue-600 transition-colors">Features</a>
              <a href="#industries" className="text-gray-600 hover:text-blue-600 transition-colors">Industries</a>
              <Link to="/pricing" className="text-gray-600 hover:text-blue-600 transition-colors">Pricing</Link>
              <a href="#contact" className="text-gray-600 hover:text-blue-600 transition-colors">Contact</a>
            </nav>
            <button 
              onClick={handleScheduleDemo}
              className="bg-cyan-500 text-white px-4 py-2 rounded-2xl hover:bg-cyan-600 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Request Demo
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-gray-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-emerald-100 px-4 py-2 rounded-full mb-6">
                <HardHat className="h-4 w-4 text-emerald-600" />
                <span className="text-sm font-semibold text-emerald-700">Industrial & Field Operations</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                EHS-Ready Phone Monitoring for Industrial Sites
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Document OSHA compliance, reduce distraction-related incidents, and monitor dispersed field teams with work-hours-only phone activity tracking. Built for EHS managers overseeing high-risk operations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={handleScheduleDemo}
                  className="bg-cyan-500 text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-cyan-600 transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
                >
                  <span>Schedule Demo</span>
                  <ArrowRight className="h-5 w-5" />
                </button>
                <Link 
                  to="/pricing"
                  className="border-2 border-cyan-500 text-cyan-500 px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-cyan-500 hover:text-white transition-all duration-300 text-center shadow-lg hover:shadow-xl"
                >
                  View Pricing
                </Link>
              </div>
            </div>
            <div className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Critical for Safety Managers</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-emerald-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Prevent Distraction Accidents</h4>
                    <p className="text-gray-600">Real-time monitoring near equipment and hazardous zones</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-emerald-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">OSHA-Ready Documentation</h4>
                    <p className="text-gray-600">Automated compliance logs for safety audits and inspections</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-emerald-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Field & Remote Visibility</h4>
                    <p className="text-gray-600">Monitor dispersed teams across multiple job sites</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-emerald-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Supervisor Dashboard</h4>
                    <p className="text-gray-600">Live insights for EHS managers and operations leads</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-cyan-600 mb-2">Inattention Hazards</div>
              <p className="text-gray-600">Worker inattention contributes to preventable industrial accidents and near-misses</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-cyan-600 mb-2">High Injury Costs</div>
              <p className="text-gray-600">Serious workplace injuries create significant financial and operational impact</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-cyan-600 mb-2">Elevated Risk</div>
              <p className="text-gray-600">Phone distraction substantially increases accident probability in hazardous environments</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features for Warehouse */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Built for EHS & Operations Managers
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive phone monitoring designed for safety-critical environments
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100">
              <div className="bg-cyan-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <Clock className="h-7 w-7 text-cyan-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Shift-Based Monitoring</h3>
              <p className="text-gray-600">
                Automatically activates during work shifts, pauses during breaks, and stops after clock-out.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100">
              <div className="bg-emerald-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <AlertTriangle className="h-7 w-7 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Instant Safety Alerts</h3>
              <p className="text-gray-600">
                Supervisors receive real-time notifications when excessive usage is detected in hazard zones.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100">
              <div className="bg-violet-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <FileText className="h-7 w-7 text-violet-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Compliance Reports</h3>
              <p className="text-gray-600">
                Automated timestamped logs ready for OSHA inspections, safety audits, and incident reports.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100">
              <div className="bg-amber-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <BarChart3 className="h-7 w-7 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">EHS Analytics</h3>
              <p className="text-gray-600">
                Track trends, identify high-risk behaviors, and measure safety program effectiveness.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100">
              <div className="bg-indigo-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <Eye className="h-7 w-7 text-indigo-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Multi-Site Dashboard</h3>
              <p className="text-gray-600">
                Centralized view for safety managers overseeing field teams and dispersed job sites.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100">
              <div className="bg-rose-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <Shield className="h-7 w-7 text-rose-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Privacy-Conscious</h3>
              <p className="text-gray-600">
                Work hours only, with clear employee notice. No message content or personal data access.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section id="industries" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Industries We Serve
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Trusted by safety managers in high-risk and compliance-heavy operations
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-6 rounded-2xl border border-cyan-100">
              <Wrench className="h-10 w-10 text-cyan-600 mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">Manufacturing</h3>
              <p className="text-sm text-gray-600">Assembly lines, machinery operation, quality control</p>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-6 rounded-2xl border border-emerald-100">
              <HardHat className="h-10 w-10 text-emerald-600 mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">Construction</h3>
              <p className="text-sm text-gray-600">Job sites, heavy equipment, field crews</p>
            </div>

            <div className="bg-gradient-to-br from-violet-50 to-purple-50 p-6 rounded-2xl border border-violet-100">
              <Activity className="h-10 w-10 text-violet-600 mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">Logistics</h3>
              <p className="text-sm text-gray-600">Distribution centers, freight, transportation</p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-6 rounded-2xl border border-amber-100">
              <Users className="h-10 w-10 text-amber-600 mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">Field Service</h3>
              <p className="text-sm text-gray-600">Utilities, maintenance, remote technicians</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Measurable Safety Improvements
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Real outcomes for EHS programs and operational efficiency
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100">
              <div className="bg-emerald-100 w-20 h-20 rounded-3xl flex items-center justify-center mb-6 shadow-lg">
                <TrendingUp className="h-8 w-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Reduce Accident Rates</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Lower distraction-related incidents</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Decrease workers' compensation claims</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Improve OSHA recordable metrics</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100">
              <div className="bg-cyan-100 w-20 h-20 rounded-3xl flex items-center justify-center mb-6 shadow-lg">
                <Shield className="h-8 w-8 text-cyan-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Strengthen Compliance</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <span>Maintain audit-ready documentation</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <span>Demonstrate duty of care to regulators</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <span>Protect against negligence liability</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100">
              <div className="bg-violet-100 w-20 h-20 rounded-3xl flex items-center justify-center mb-6 shadow-lg">
                <TrendingUp className="h-8 w-8 text-violet-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Improve Operations</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-violet-500 mt-0.5 flex-shrink-0" />
                  <span>Increase workforce focus and productivity</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-violet-500 mt-0.5 flex-shrink-0" />
                  <span>Reduce costly downtime from incidents</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-violet-500 mt-0.5 flex-shrink-0" />
                  <span>Build culture of safety accountability</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Industrial Safety Monitoring FAQs
            </h2>
            <p className="text-xl text-gray-600">
              Answers for EHS managers evaluating workplace phone monitoring
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Is monitoring limited to work hours and job sites?</h3>
              <p className="text-gray-700">
                Yes. Monitoring is configured by the employer to activate only during scheduled work hours. Off-site travel, breaks, and all personal time remain completely private. Field teams are monitored only when on-duty at designated work locations.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Does Nix Spaces access employee message content?</h3>
              <p className="text-gray-700">
                No. Nix Spaces logs <em>when</em> and <em>how long</em> a phone is actively used—it does not read texts, emails, view photos, or access any personal content. The system captures activity timestamps and duration only, not the substance of communications.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">How are employees notified about monitoring?</h3>
              <p className="text-gray-700">
                Employers must provide clear written notice to all employees subject to monitoring before implementation. The app displays on-device indicators when monitoring is active. Employee awareness and transparency are essential to compliant deployment.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Can EHS managers monitor multiple job sites remotely?</h3>
              <p className="text-gray-700">
                Yes. The centralized dashboard provides real-time visibility across all monitored locations. EHS managers can view activity by site, shift, or individual employee, making it practical for overseeing dispersed field crews and multiple facilities from one interface.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">What industries use Nix Spaces?</h3>
              <p className="text-gray-700">
                Nix Spaces is designed for manufacturing, construction, field service, utilities, and logistics—any industry where phone distraction near equipment or in hazard zones creates elevated safety risk. It's purpose-built for EHS-focused environments.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Are logs suitable for OSHA inspections and incident reports?</h3>
              <p className="text-gray-700">
                Yes. All monitoring logs are timestamped, tamper-evident, and exportable. They document when monitoring was active, what usage occurred, and when alerts were triggered—ready for OSHA inspections, post-incident investigations, and safety audits.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">What is the typical deployment timeline?</h3>
              <p className="text-gray-700">
                Most organizations complete deployment in 1–3 weeks, including policy review, employee communication, app provisioning, and EHS team training. We provide onboarding support and best-practice guidance throughout the rollout process.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Credibility Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-8 md:p-12 rounded-3xl border border-gray-200">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                From the Team Behind GoPoint NIX
              </h2>
              <p className="text-lg text-gray-700">
                Nix Spaces is built by the proven team behind <strong>GoPoint NIX</strong>, an established distracted-driving prevention solution trusted in fleet management and transportation safety.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-2xl shadow-md">
                <h3 className="font-semibold text-gray-900 mb-2">Safety Expertise</h3>
                <p className="text-gray-700">Years of proven experience building phone monitoring technology for high-risk, safety-critical industries.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-md">
                <h3 className="font-semibold text-gray-900 mb-2">Privacy by Design</h3>
                <p className="text-gray-700">Work-hours-only monitoring with transparent employee notice, no content monitoring, and clear data boundaries.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Talk to a Safety Specialist</h2>
            <p className="text-xl text-gray-600">
              See how Nix Spaces helps EHS managers reduce accidents and improve compliance.
            </p>
          </div>
          
          <div className="text-center">
            <button 
              onClick={handleScheduleDemo}
              className="bg-cyan-500 text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-cyan-600 transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl mx-auto"
            >
              <span>Schedule Demo</span>
              <ChevronRight className="h-5 w-5" />
            </button>
            <p className="text-gray-500 mt-4">Free consultation • No obligation • OSHA-focused discussion</p>
          </div>
        </div>
      </section>

      {/* Policy Kit CTA */}
      <section className="py-16 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-cyan-100 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-cyan-500 mb-6">
              <FileText className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Start with a Clear Policy
            </h2>
            <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
              Before rolling out monitoring tools, establish workplace expectations with our free Shift Distraction Policy Kit. Ready-to-customize templates for industrial sites, including monitoring notice language (complete with legal counsel).
            </p>
            <Link
              to="/shift-distraction-policy-kit"
              className="inline-flex items-center justify-center space-x-2 bg-cyan-500 text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-cyan-600 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <FileText className="h-5 w-5" />
              <span>Download Free Policy Kit</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <Link to="/" className="flex items-center space-x-3">
              <img src="/image.png" alt="Nix Logo" className="h-8 w-auto filter invert" />
              <span className="text-xl font-bold">Spaces</span>
            </Link>
            
            <div className="flex flex-wrap items-center justify-center space-x-6 text-sm">
              <Link to="/" className="text-gray-400 hover:text-white transition-colors">
                Home
              </Link>
              <Link to="/pricing" className="text-gray-400 hover:text-white transition-colors">
                Pricing
              </Link>
              <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">
                Terms of Service
              </Link>
              <span className="text-gray-400">
                © 2024 Nix Spaces. All rights reserved.
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default IndustrialSafety;
